/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
'use client';

import 'client-only';
import { clsx } from '@accelint/design-foundation/lib/utils';
import { TextContext } from 'react-aria-components/Text';
import { Provider } from 'react-aria-components/slots';
import { HeadingContext } from 'react-aria-components/Heading';
import { AvatarContext } from '../avatar/context';
import { IconContext } from '../icon/context';
import styles from './styles.module.css';
import type { SidenavAvatarProps } from './types';

/**
 * SidenavAvatar - Avatar component for sidenav
 *
 * Provides an avatar container with proper styling for the sidenav
 *
 * @example
 * ```tsx
 * <SidenavHeader>
 *   <SidenavAvatar>
 *     <Avatar>JD</Avatar>
 *     <Heading>John Doe</Heading>
 *     <Text>Administrator</Text>
 *   </SidenavAvatar>
 * </SidenavHeader>
 * ```
 *
 * @param props - {@link SidenavAvatarProps}
 * @param props.children - Avatar content (icon, heading, text).
 * @param props.className - CSS class for the avatar container.
 * @returns The rendered SidenavAvatar component.
 */
export function SidenavAvatar({
  children,
  className,
  ...rest
}: SidenavAvatarProps) {
  return (
    <Provider
      values={[
        [IconContext, { size: 'large', className: styles.icon }],
        [HeadingContext, { className: clsx(styles.heading, styles.transient) }],
        [TextContext, { className: clsx(styles.text, styles.transient) }],
        [AvatarContext, { classNames: { avatar: styles.icon } }],
      ]}
    >
      <div {...rest} className={clsx(styles.avatar, className)}>
        {children}
      </div>
    </Provider>
  );
}
